import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  AutoCompleteSelectUI,
  DataGridIdentifiers,
  DeleteDialogUI,
  FileTabUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
  TabsUI,
} from '../../Interface';
import ProductionOperations from './ProductionOperations';
import { Field, useFormikContext } from 'formik';
import ProductionItem from './ProductionItem';
import jokerService from '../../../service/joker.service';
import { JokerTypes } from '../../../config/joker';
import JokerTab from '../../Interface/Joker/JokerTab';
import ProductionIssues from './ProductionIssues';
import ProductionReceipt from './ProductionReceipt';
import ProductionQuantities from './ProductionQuantities';
import useConfig from '../../../context/ConfigContext';
import ProductionConfirmation from './ProductionConfirmation';
import { FormType } from '../../../config/form';
import supportingDocumentTypeService from '../../../service/supportingDocumentType.service';
import { SupportingDocumentSource } from '../../../config/supportingDocument';
import { generateAutocompleteItems } from '../../../utils/helper';

const DescriptionPanel = (props) => {
  return (
    <div>
      <div className="col-span-full relative">
        <div className="mt-2">
          <Field
            type="text"
            name="description"
            as={InputUI}
            className={`${props.disabled ? 'rounded-md' : ''} resize-none h-36, p-2`}
            fullWidth
            label={<FormLabelUI text="Leírás" />}
            variant="standard"
            helperText={<FormErrorUI message={props.errors.description} />}
            disabled={props.disabled}
            InputLabelProps={{ shrink: props.values.description !== '' }}
            multiline
            rows={4}
          />
        </div>
      </div>
    </div>
  );
};

const ProductionTabs = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const { config, getConfig } = useConfig();
  const { selectedValues, disabled, params } = props;
  const [activeTab, setActiveTab] = useState(0);
  const [jokerGroups, setJokerGroups] = useState([]);
  const [deleteDialog, setDeletDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [supportingDocumentTypeOptions, setSupportingDocumentTypeOptions] = useState([]);

  const getJokerGroups = async () => {
    jokerService.getGroups(JokerTypes.PRODUCTION).then((data) => {
      setJokerGroups(data);
    });
  };

  useEffect(() => {
    getJokerGroups();
    if (!Object.keys(config).length) getConfig();
  }, []);

  useEffect(() => {
    if (params?.ENABLE_SUPPORTING_DOC !== 'Y') {
      setSupportingDocumentTypeOptions([]);
      return;
    }

    supportingDocumentTypeService.getExternalItems().then((data) => {
      const productionInstructionTypes = (data ?? []).filter(
        (type) =>
          type.generated === false && type.source === SupportingDocumentSource.FINISHED_PRODUCT_PRODUCTION_INSTRUCTION
      );

      setSupportingDocumentTypeOptions(generateAutocompleteItems(productionInstructionTypes, 'name', 'id'));
    });
  }, [params?.ENABLE_SUPPORTING_DOC]);

  const handleUpload = (files) => {
    const filesWithId = files.map((file) => {
      file.id = uuidv4();
      file.supportingDocumentTypeId = null;
      return file;
    });

    const existingAttachments = Array.isArray(values.productionAttachment) ? values.productionAttachment : [];

    setFieldValue('productionAttachment', [...existingAttachments, ...filesWithId]);
  };

  const handleSupportingDocumentTypeChange = (row, supportingDocumentTypeId) => {
    setFieldValue(
      'productionAttachment',
      (values.productionAttachment || []).map((attachment) => {
        if (attachment.id !== row.id) return attachment;

        if (attachment instanceof File) {
          attachment.supportingDocumentTypeId = supportingDocumentTypeId;
          return attachment;
        }

        return {
          ...attachment,
          supportingDocumentTypeId,
        };
      })
    );
  };

  const hideDeleteDialog = () => {
    setDeletDialog(false);
  };

  const handleRemoveElement = () => {
    setFieldValue(
      'productionAttachment',
      (values.productionAttachment || []).filter((productionAttachment) => productionAttachment.id !== fileToDelete.id)
    );

    setFileToDelete(null);
    hideDeleteDialog();
  };

  const tabs = [
    {
      id: 0,
      name: 'Műveletek',
      content: <ProductionOperations {...props} />,
    },
    {
      id: 1,
      name: 'Cikkek',
      content: <ProductionItem {...props} />,
    },
    {
      id: 2,
      name: 'Megjegyzés',
      content: <DescriptionPanel {...props} />,
    },
    {
      id: 3,
      name: 'Csatolmányok',
      content: (
        <FileTabUI
          datagridIdentifier={
            params?.ENABLE_SUPPORTING_DOC === 'Y'
              ? DataGridIdentifiers.uploadFileWithSourceAndSupportingDocument
              : DataGridIdentifiers.uploadFileWithSource
          }
          savedFiles={[
            ...(values?.productionAttachment ?? []),
            ...(selectedValues.sourceListAttachment ?? []),
            ...(selectedValues.itemAttachment ?? []),
            ...(selectedValues.projectAttachment ?? []),
          ]}
          parentObj={FormType.PRODUCTION}
          handleUpload={handleUpload}
          disabled={disabled}
          handleDelete={(file) => {
            setFileToDelete(file);
            setDeletDialog(true);
          }}
          additionalColumns={
            params?.ENABLE_SUPPORTING_DOC === 'Y'
              ? [
                  {
                    field: 'supportingDocumentTypeId',
                    headerName: 'Kísérő dokumentum típus',
                    flex: 0.8,
                    sortable: false,
                    renderCell: (params) => {
                      if (!(values.productionAttachment ?? []).includes(params.row)) return null;

                      return (
                        <div className="w-full" onClick={(event) => event.stopPropagation()}>
                          <AutoCompleteSelectUI
                            id={`supportingDocumentTypeId-${params.row.id}`}
                            dataset={supportingDocumentTypeOptions}
                            selectedValue={params.row.supportingDocumentTypeId}
                            selectedLabelValue={params.row.supportingDocumentType?.name}
                            disabled={disabled}
                            onChange={(_event, value) =>
                              handleSupportingDocumentTypeChange(params.row, value?.value ?? null)
                            }
                          />
                        </div>
                      );
                    },
                  },
                ]
              : []
          }
        />
      ),
    },
    {
      id: 4,
      name: 'Lejelelentések',
      content: <ProductionConfirmation {...props} />,
    },
    //feltételessé tenni, hogy a a config tartalmazza-e a gyártási kiadás funkciót
    ...(config?.productionStockAccounting
      ? [
          {
            id: 5,
            name: 'Alapanyag',
            content: <ProductionIssues {...props} />,
          },
          {
            id: 6,
            name: 'Bevételezés',
            content: <ProductionReceipt {...props} />,
          },
        ]
      : []),

    {
      id: 7,
      name: 'Összefoglaló',
      content: <ProductionQuantities {...props} />,
    },
    ...((jokerGroups.length > 0 &&
      jokerGroups
        .filter((group) => group.jokerFields.length > 0)
        .map((group, index) => ({
          id: index + 8,
          name: group.name,
          content: <JokerTab fields={group.jokerFields} key={index} {...props} />,
        }))) ||
      []),
  ];

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  return (
    <div className="my-4 mt-6">
      <div className="px-3">
        <TabsUI activeTab={activeTab} handleTabClick={handleTabClick}>
          {tabs.map((tab, index) => (
            <div key={index} name={tab.name}>
              {tab.content}
            </div>
          ))}
        </TabsUI>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={deleteDialog}
        onHide={() => {
          hideDeleteDialog();
          setFileToDelete(null);
        }}
      />
    </div>
  );
};

export default ProductionTabs;
